import React, {useContext, useState} from 'react';
import {
    Modal, Box, SpaceBetween, Button, FormField, FileDropzone, FileInput, FileTokenGroup, Alert
} from '@amzn/awsui-components-react';
import { GlobalAppContext } from 'src/components/App';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';

interface UpdateMDASheetModalProps {
    sheetName: string;
    sheetId: string;
    fileUrl: string;
    status: WorksheetStatus;
}

export default function UpdateMDASheetModal({
    sheetName, 
    sheetId,
    fileUrl,
    status
}: UpdateMDASheetModalProps) {
    const [visible, setVisible] = useState(false);
    const [allocationSheets, setAllocationSheets] = useState<File[]>([]);
    const [isSubmitting, setSubmitting] = useState(false);
    const [submitErrors, setSubmitErrors] = useState<string | undefined>(undefined);
    const [fileError, setFileError] = useState<string | undefined>(undefined);

    const service = useContext(GlobalAppContext).services.multidimensionalAllocationService;

    // Handle file selection
    const handleFileChange = (files: File[]) => {
        setAllocationSheets(files);
        setFileError(undefined); // Clear error when file is selected
    };

    // Handle file dismiss
    const handleFileDismiss = (fileIndex: number) => {
        const updatedFiles = allocationSheets.filter((_, index) => index !== fileIndex);
        setAllocationSheets(updatedFiles);
    };

    // Validate files
    const validateFiles = (): boolean => {
        if (allocationSheets.length === 0) {
            setFileError("Please select a file to upload");
            return false;
        }
        setFileError(undefined);
        return true;
    };

    const handleSubmit = async () => {
        /* Submission has three steps:
            - file validation
            - upload the file to s3. 
            - create/update the allocationSheet in ATP (with metadata from the uploaded s3 file).
        */

        setSubmitErrors(undefined); // clear all previous errors

        // Validate only files since other values come from props
        if (!validateFiles()) {
            return;
        }

        try {
            setSubmitting(true);
            const s3_key = `${sheetName.replace(/\s+/g, '_')}_${Date.now()}` // sample: SheetName_213214215132
            await service.uploadMultiAllocationSheet(s3_key, allocationSheets[0])

            // Call the backend update API after successful S3 upload
            const updateAllocationSheetPayload = {
                sheetId: sheetId,
                fileUrl: s3_key
            };

            const response = await service.updateMultiAllocationSheet(updateAllocationSheetPayload);

            setSubmitErrors(undefined);
            setVisible(false);
            window.location.reload();
        } catch (err) {
            const errMsg = err instanceof Error ? `${err.message}` : `${err}`
            setSubmitErrors(errMsg);
            return;
        } finally {
            setSubmitting(false)
        }
    };

    // Handle cancel
    const handleCancel = () => {
        setAllocationSheets([]);
        setSubmitErrors(undefined);
        setFileError(undefined);
        setVisible(false);
    };

    // Handle open modal
    const handleOpenModal = () => {
        setVisible(true);
    };

    const isDisabled = status === "PENDING_APPROVAL";

    return (<>
        {/* Trigger Button */}
        <Button 
            onClick={handleOpenModal}
            disabled={isDisabled}
        >
            Upload new version
        </Button>
        <Modal
        onDismiss={(event) => {
            event.detail.reason !== 'overlay' && handleCancel()
        }}
        visible={visible}
        closeAriaLabel="Close modal"
        size="medium"
        footer={<Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    Update sheet
                </Button>
            </SpaceBetween>
        </Box>}
        header="Update multidimensional allocation sheet"
    >
        <SpaceBetween direction="vertical" size="m">
            {"Once uploaded, the new version will overwrite and replace the existing allocation sheet."}

            {/* File Upload Field */}
            <FormField 
                label="Allocation sheet"
                errorText={fileError}
            >
                <FileDropzone
                    onChange={({detail}) => handleFileChange(detail.value)}
                >
                    <SpaceBetween size="xxs" alignItems="center">
                        <Box color="inherit">
                            Drag and drop files, or select from below.
                        </Box>
                        <FileInput
                            value={allocationSheets}
                            onChange={({detail}) => handleFileChange(detail.value)}
                            multiple={true}
                            accept=".xlsx,.xls"
                        >
                            Choose files
                        </FileInput>
                    </SpaceBetween>
                </FileDropzone>

                {allocationSheets.length > 0 && (<Box margin={{top: 's'}}>
                    <FileTokenGroup
                        items={allocationSheets.map(file => ({file}))}
                        onDismiss={({detail}) => handleFileDismiss(detail.fileIndex)}
                        i18nStrings={{
                            limitShowFewer: "Show fewer files",
                            limitShowMore: "Show more files",
                            errorIconAriaLabel: "Error",
                            warningIconAriaLabel: "Warning"
                        }}
                    />
                </Box>)}
            </FormField>
            {!!submitErrors && <Alert type="error" dismissible header="Update failed."> {submitErrors} </Alert>}

        </SpaceBetween>
    </Modal>
    </>);
}
